import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import classnames from 'classnames';
import { PapiExtendedDeviation } from '@wix/da-papi-types';
import FoldableTagList from '@wix/da-ds/pkg/TagList/FoldableTagList';
import { useSubscriptionTier } from '../../../utils/hooks/useSubscriptionTier';
import License from '../../../License';
import TextRenderer from '../../../TextRenderer/TextRenderer';
import { DetailsProps } from '../types';
import DeviationAttributeCSV from '../_partials/DeviationAttributeCSV';
import { BiLoggerContextProvider } from '@wix/da-bi/pkg/BiLogger.context';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import { isExtendedDeviationFullyLoaded } from '../../../DeviationPage/redux/helpers';
import GruserZoneLocal from '../../GruserZone/Local';
import DescViewer from '@wix/da-editor-tiptap/pkg/components/Viewer/presets/DeviationDescription';
import { MeasuredCookieType } from '@wix/da-hooks/pkg/useMeasureElement/redux/types';

import s from './Literature.scss';

export const LiteratureDetails: React.FC<DetailsProps> = ({
  deviation,
  deviation: {
    author,
    publishedTime,
    license,
    extended: { descriptionText, tags } = {} as PapiExtendedDeviation,
  },
  className,
  contentClassName,
}) => {
  const isMobile = useContext(MobileContext);
  const { t } = useTranslation();
  const isExtendedLoaded = isExtendedDeviationFullyLoaded(deviation);
  const { isSubscribedButBlocked, needsSubscription } = useSubscriptionTier(deviation);
  const isBlocked = needsSubscription || isSubscribedButBlocked;

  const isDescriptionTextShown = !!descriptionText?.html?.markup && !isBlocked;

  return (
    <div className={classnames(s['root'], className, contentClassName)}>
      {isExtendedLoaded && (
        <>
          {tags && (
            <div className={s['tags']}>
              <FoldableTagList
                tags={tags}
                moreLabel={
                  <span title={t('common.showMore')} aria-label={t('common.showMore')}>
                    {t('common.moreEllipsis')}
                  </span>
                }
              />
            </div>
          )}

          {isDescriptionTextShown && (
            <TextRenderer
              // adding a key to force a render when the deviation changes
              // in duperbrowse situations, otherwise, daEditor squelches re-renders
              // on an already mounted Viewer - even if the content changes
              key={`description-${deviation.deviationId}`}
              textContent={descriptionText}
              className={s['description']}
              legacyTheme="literature-description"
              withSafetyCheckModal
              tipTapViewer={DescViewer}
              cookieType={MeasuredCookieType.EDITOR_POLL}
              defaultSize={{
                desktop: { width: 780, height: 40 },
                mobile: { width: 380, height: 80 },
              }}
            />
          )}
          <div className={s['misc']}>
            <DeviationAttributeCSV deviation={deviation} />

            <div className={s['license']}>
              <License
                license={license}
                author={author}
                publishedTime={publishedTime}
                showSymbol={false}
              />
            </div>
          </div>
        </>
      )}
      {!isMobile && (
        <BiLoggerContextProvider value={{ creatorid: deviation.author?.useridUuid }}>
          <GruserZoneLocal deviation={deviation} />
        </BiLoggerContextProvider>
      )}
    </div>
  );
};
LiteratureDetails.displayName = 'LiteratureDetails';

export default LiteratureDetails;
